/* ------------------------------------------------------------------
  File: Tag.jsx
  Author: Engr. Shaffa K. Kokro Jr. - CEO, Accend Holdings Ltd.
  Last Modified: 2026-05-18
------------------------------------------------------------------ */

// Purpose:
//   - Provides a reusable tag/chip component for the Accend Holdings website.
//   - Supports topic labels, keyword labels, filter chips, selected state,
//     removable state, optional icons, size variants, and visual variants.
//   - Uses the acc-tag namespace to avoid conflicts with Bootstrap.

/**
 * Tag
 *
 * A reusable tag/chip component for displaying topics, keywords, and filters.
 *
 * @param {React.ReactNode} children
 * The tag text or content.
 *
 * @param {React.ReactNode} icon
 * Optional icon displayed before the label.
 *
 * @param {"default" | "brand" | "accent" | "neutral" | "inverse"} [variant="default"]
 * Controls the tag visual style.
 *
 * @param {"sm" | "md"} [size="sm"]
 * Controls the tag size.
 *
 * @param {"span" | "button" | "div"} [as="span"]
 * Controls the HTML element used to render the tag.
 *
 * @param {boolean} [selected=false]
 * Applies selected/active styling.
 *
 * @param {boolean} [removable=false]
 * Shows a remove button inside the tag.
 *
 * @param {Function} [onRemove]
 * Function called when the remove button is clicked.
 *
 * @param {string} [removeLabel]
 * Accessible label for the remove button.
 *
 * @param {string} [className=""]
 * Additional custom class names.
 */

import "./Tag.css";
import { LuX } from "react-icons/lu";

const Tag = ({
  children,
  icon,
  variant = "default",
  size = "sm",
  as: Component = "span",
  selected = false,
  removable = false,
  onRemove,
  removeLabel,
  className = "",
  ...props
}) => {
  const tagClasses = [
    "acc-tag",
    `acc-tag--${variant}`,
    `acc-tag--${size}`,
    selected ? "acc-tag--selected" : "",
    removable ? "acc-tag--removable" : "",
    icon ? "acc-tag--with-icon" : "",
    className,
  ]
    .filter(Boolean)
    .join(" ");

  const labelText = typeof children === "string" ? children : "tag";

  const handleRemove = (event) => {
    event.stopPropagation();

    if (onRemove) {
      onRemove(event);
    }
  };

  return (
    <Component className={tagClasses} {...props}>
      {icon && (
        <span className="acc-tag__icon" aria-hidden="true">
          {icon}
        </span>
      )}

      <span className="acc-tag__label">{children}</span>

      {removable && (
        <button
          type="button"
          className="acc-tag__remove"
          aria-label={removeLabel || `Remove ${labelText}`}
          onClick={handleRemove}
        >
          <LuX />
        </button>
      )}
    </Component>
  );
};

export default Tag;